import { format, parseISO } from 'date-fns'
import {
  AbsoluteFill,
  Easing,
  interpolate,
  useCurrentFrame,
  useVideoConfig,
} from 'remotion'
import ZealousSvg from '../assets/ZealousSvg'
import HorizontalAvatars from '../components/HorizontalParticipants'
import {
  ResponsiveSceneProvider,
  useResponsiveSceneProps,
} from '../components/ResponsiveSceneContext'
import StarSvg from '../components/StarSvg'
import VerticalAvatars from '../components/VerticalParticipants'
import { SceneProps } from '../types'
import { cn } from '../utils/cn'
import { formatTime } from '../utils/formatTime'
import { useOrientation } from '../utils/useOrientation'

export function TitleScene({ audiogram, orientation, scene }: SceneProps) {
  const hosts =
    audiogram.speakers
      ?.filter((s) => ['HOST', 'COHOST'].includes(s.role!) && s.avatarUrl)
      .slice(0, 3) ?? []

  return (
    <ResponsiveSceneProvider
      audiogram={audiogram}
      scene={scene}
      orientation={orientation}
    >
      <Background />
      <EdgeStripe />

      <DateRecorded />
      <DurationOfRecording />
      <HostedBy />

      <StarLineA />
      <StarLineB />

      <TitleText />

      {orientation === 'landscape' ? (
        <HorizontalAvatars speakers={hosts} />
      ) : (
        <VerticalAvatars speakers={hosts} />
      )}

      {/* <Watermark /> */}
    </ResponsiveSceneProvider>
  )
}

const Background = () => {
  const { audiogram } = useResponsiveSceneProps()

  return (
    <AbsoluteFill style={{ backgroundColor: audiogram.theme.colors.primary }} />
  )
}

const Watermark = () => {
  const { audiogram, orientation } = useResponsiveSceneProps()
  const { p, s, l, lp, sp } = useOrientation(orientation)
  return (
    <ZealousSvg
      className={cn(
        'absolute right-[60px] h-[49px] opacity-50',
        l && 'bottom-[63px]',
        sp && 'top-[60px]',
      )}
      color={audiogram.theme.colors.secondary}
    />
  )
}

const DateRecorded = () => {
  const { audiogram, u, w } = useResponsiveSceneProps()

  return (
    <div
      className="absolute"
      style={{
        color: audiogram.theme.colors.secondary,
        fontSize: u(1),
        left: u(2),
        top: u(3),
        fontFamily: audiogram.theme.fonts.primary,
        transform: 'translateY(-50%)',
      }}
    >
      {!!audiogram.episode.startedAt &&
        format(parseISO(audiogram.episode.startedAt), 'MMM d, H:mm')}
    </div>
  )
}

const DurationOfRecording = () => {
  const { audiogram, u, w } = useResponsiveSceneProps()

  return (
    <div
      className="absolute"
      style={{
        color: audiogram.theme.colors.secondary,
        fontSize: u(1),
        left: w() - u(2),
        top: u(3),
        fontFamily: audiogram.theme.fonts.primary,
        transform: 'translate(-100%,-50%)',
      }}
    >
      {!!audiogram.episode.duration && formatTime(audiogram.episode.duration)}
    </div>
  )
}

const HostedBy = () => {
  const { audiogram, u, w, h, orientation } = useResponsiveSceneProps()

  const bottom = {
    landscape: u(8),
    square: u(13),
    portrait: u(19),
  }[orientation]

  return (
    <div
      className="absolute"
      style={{
        color: audiogram.theme.colors.secondary,
        opacity: 0.5,
        fontSize: u(1.25),
        fontFamily: audiogram.theme.fonts.primary,
        left: u(4),
        bottom,
        transform: `translateY(50%)`,
      }}
    >
      hosted by
    </div>
  )
}

const StarLineA = () => {
  const { audiogram, u, w, h } = useResponsiveSceneProps()
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()

  const time = frame / fps

  const rate = 0.25 * 360 * time

  const enterDriver = interpolate(time, [0, 1], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
    easing: Easing.out(Easing.exp),
  })

  const x = enterDriver * (w() - u(2))

  return (
    <>
      <div
        className="absolute -translate-y-1/2"
        style={{
          backgroundColor: audiogram.theme.colors.secondary,
          top: u(6),
          width: x,
          height: u(0.125),
        }}
      />
      <StarSvg
        className="absolute"
        style={{
          color: audiogram.theme.colors.secondary,
          top: u(6),
          width: u(3),
          height: u(3),
          transform: `translate(-50%,-50%) translateX(${x}px) rotate(${-rate}deg)`,
        }}
      />
    </>
  )
}

const StarLineB = () => {
  const { audiogram, u, w, h, orientation } = useResponsiveSceneProps()
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()

  const time = frame / fps

  const rate = 0.25 * 360 * time

  const enterDriver = interpolate(time, [0, 1], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
    easing: Easing.out(Easing.exp),
  })

  const bottom = {
    landscape: u(8),
    square: u(13),
    portrait: u(19),
  }[orientation]
  const x = enterDriver * (w() - u(12))

  return (
    <>
      <div
        className="absolute -translate-x-full translate-y-1/2"
        style={{
          backgroundColor: audiogram.theme.colors.secondary,
          bottom,
          left: w(),
          width: x,
          height: u(0.125),
        }}
      />
      <StarSvg
        className="absolute"
        style={{
          color: audiogram.theme.colors.secondary,
          height: u(3),
          width: u(3),
          bottom,
          right: 0,
          transform: `translate(50%,50%) translateX(${-x}px) rotate(${rate}deg)`,
        }}
      />
    </>
  )
}

const TitleText = () => {
  const { audiogram, u, w, h, orientation } = useResponsiveSceneProps()

  const title = audiogram.episode.title

  if (!title) return null

  const bottom = {
    landscape: u(11),
    square: u(16),
    portrait: u(24),
  }[orientation]

  return (
    <div
      className="absolute flex flex-col justify-center overflow-hidden"
      style={{
        color: audiogram.theme.colors.secondary,
        right: u(2),
        bottom,
        left: u(4),
        top: u(9),
        fontFamily: audiogram.theme.fonts.primary,
        fontSize: (orientation === 'square' ? 3000 : 5000) / title.length,
        lineHeight: 1.2,
      }}
    >
      {title}
    </div>
  )
}

const EdgeStripe = () => {
  const { audiogram, u } = useResponsiveSceneProps()
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()
  const time = frame / fps
  const scale = interpolate(time - 0.1, [0, 1], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
    easing: Easing.out(Easing.exp),
  })

  return (
    <div
      className="absolute"
      style={{
        backgroundColor: audiogram.theme.colors.accent,
        top: u(6),
        bottom: 0,
        left: 0,
        width: u(2),
        transform: `translate(-50%,50%) scaleY(${scale}) translate(50%,-50%)`,
        opacity: scale,
      }}
    />
  )
}
